import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import EntitySelectDialog from '../../components/EntitySelectDialog/EntitySelectDialog';
import { ReduxState } from '../../redux/types';
import CompanyLocation from '../../types/CompanyLocation';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import CompanyLocationDialog from '../CompanyLocationDialog/CompanyLocationDialog';

/*
 * Show the list of locations of a company.
 */

interface Props {
    open: boolean;
    onClose: (selectedId?: number) => void;
    companyId: number;
    locations: CompanyLocation[];
    updateData: () => void;
    isLoading?: boolean;
}

const CompanyLocationsDialog = (props: Props) => {
    const { open, onClose, companyId, locations, updateData, isLoading } = props;

    const { t } = useTranslation();
    const isAdmin = useSelector((state: ReduxState) => state.user.type) === 'admin';

    const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
    const [selectedLocationId, selectLocationId] = useState(0);
    const [localIsLoading, setLocalIsLoading] = useState(false);

    const [openDialog, closeDialog] = useDialog();

    const openLocationDialog = useCallback((locationId?: number) => {
        setIsLocationDialogOpen(true);
        selectLocationId(locationId ?? 0);
    }, [selectLocationId, setIsLocationDialogOpen]);

    const closeLocationDialog = useCallback((locationId?: number) => {
        setIsLocationDialogOpen(false);

        if (locationId) {
            updateData();
        }
    }, [setIsLocationDialogOpen, updateData]);

    const onCloseWrapper = useCallback((locationId?: number) => {
        // if the user clicks on a location, show its details, otherwise close the dialog
        return locationId ? openLocationDialog(locationId) : onClose();
    }, [onClose, openLocationDialog]);

    const deleteLocation = useCallback((locationId: number) => {
        setLocalIsLoading(true);
        api.request('/companies/' + companyId + '/locations/' + locationId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.company_locations.delete_confirm'));
            updateData();
        }).finally(() => {
            setLocalIsLoading(false);
        });
    }, [companyId, updateData, t]);

    const deleteLocationConfirm = useCallback((locationId: number) => {
        openDialog({
            title: t('dialogs.company_locations.delete_title'),
            message: t('dialogs.company_locations.delete_message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteLocation(locationId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteLocation, openDialog, t]);

    const dataForRender = useMemo(() => {
        // locations list is arranged to be used by EntitySelectDialog component
        return locations.map(location => {
            return {
                id: location.id,
                name: location.city + ', ' + location.country.name,
                description: location.address + (location.employee ? (' (' + t('dialogs.company_locations.number_of_employee', { count: location.employee }) + ')') : '')
            };
        });
    }, [locations, t]);

    return (
        <>
            <EntitySelectDialog
                open={open}
                title={t('dialogs.company_locations.title')}
                onClose={onCloseWrapper}
                data={(isLoading || localIsLoading) ? [] : dataForRender}
                onDelete={isAdmin ? deleteLocationConfirm : undefined}
                newEntityBtnPosition='list'
                onNewEntityClick={isAdmin ? (() => openLocationDialog()) : undefined}
                newEntityBtnText={t('dialogs.company_locations.new_location')}
                isLoading={isLoading || localIsLoading}
                size='sm'
            />
            <CompanyLocationDialog open={isLocationDialogOpen} onClose={closeLocationDialog} companyId={companyId} locationId={selectedLocationId} />
        </>
    );
};

export default CompanyLocationsDialog;
